/**
 * Helpers to resolve "Prepared by" and "Approved by" signature/name pairs
 * from a document and a list of users. Used by every print view that shows
 * the signature footer (PO / PE / DN / Invoice).
 *
 *  - Prepared by  = user who created the document
 *      • PO / PE / DN: doc.created_by (user id)
 *      • Invoice:      doc.sales_rep_id  (fallback to created_by)
 *
 *  - Approved by  = the L1 + L2 approvers stored directly on the document
 *      after the two-level approval workflow completes.
 *        doc.approved_l1_by  → user id of L1 approver
 *        doc.approved_l2_by  → user id of L2 approver (may be absent if the
 *                              flow only uses Level-1 approval).
 *      Falls back to the `approval_history` entries (legacy / external
 *      stores) when the direct fields aren't present.
 *
 *      `resolveApprovers` returns an ARRAY so the print view can stamp BOTH
 *      L1 and L2 signatures when a 2-level workflow is in use.
 */

const findUser = (users, id) => {
  if (!id || !Array.isArray(users)) return null;
  return users.find((u) => u && u.id === id) || null;
};

const userToSig = (user) => {
  if (!user) return null;
  return {
    name: user.name || user.email || '',
    signature_url: user.signature_url || '',
  };
};

export const resolvePreparedBy = (doc, users) => {
  if (!doc) return null;
  // Preferred: the actual user who data-entered the document
  //   - PO / PE / DN: doc.created_by
  //   - Invoice:      doc.created_by  (newly stamped) OR doc.sales_rep_id
  //                   (legacy fallback for invoices created before created_by
  //                   was captured).
  const id = doc.created_by || doc.sales_rep_id;
  const fromUsers = userToSig(findUser(users, id));
  if (fromUsers) return fromUsers;
  if (doc.sales_rep_name) return { name: doc.sales_rep_name, signature_url: '' };
  return null;
};

/** Returns an array of { name, signature_url, level, at } for every
 *  approver that has signed off on the document. Empty array when none. */
export const resolveApprovers = (doc, users) => {
  if (!doc) return [];
  const out = [];

  const pushById = (id, level, at) => {
    if (!id) return;
    const sig = userToSig(findUser(users, id));
    if (sig) out.push({ ...sig, level, at });
  };

  // Preferred source: explicit fields stamped by the approval workflow.
  pushById(doc.approved_l1_by, 1, doc.approved_l1_at);
  pushById(doc.approved_l2_by, 2, doc.approved_l2_at);

  if (out.length > 0) return out;

  // Fallback: legacy approval_history embedded on the document.
  if (Array.isArray(doc.approval_history)) {
    const approvals = doc.approval_history.filter(
      (h) => h && (h.action === 'approve' || h.action === 'approved')
    );
    const l1 = [...approvals].reverse().find((h) => h.level === 1);
    const l2 = [...approvals].reverse().find((h) => h.level === 2);
    [l1, l2].forEach((h) => {
      if (!h) return;
      const sig = userToSig(findUser(users, h.user_id));
      out.push({
        ...(sig || { name: h.user_name || '', signature_url: '' }),
        level: h.level,
        at: h.at,
      });
    });
  }
  return out;
};

/** Legacy single-approver helper (kept for backwards compat — returns the
 *  highest-level approver only). */
export const resolveApprovedBy = (doc, users) => {
  const arr = resolveApprovers(doc, users);
  return arr.length ? arr[arr.length - 1] : null;
};
